//
//  Copyright © 2023 Block, Inc. All rights reserved.
//

import PendingPromise from '../shared/pending-promise';
import posted, { Posted } from '../../../messages/envelopes/posted';
import {
  isAppAction,
  isInternalAppAction,
  isErrorMessage,
  isRequestMessage,
  isResponseMessage,
} from '../../../messages/envelopes/action';
import {
  RequestError,
  ErrorCode,
  ErrorMessage,
  RequestMessage,
  ResponseMessage,
} from '../../../api/types/actions';
import { uuid } from '../../../utils/uuid';

/**
 * Message postable to the app from the add-on.
 *
 * RequestMessage - request to the app from the add-on.
 *
 * ResponseMessage - result from the add-on resolving a
 * request from the app.
 *
 * ErrorMessage - result from the add-on rejecting a request
 * from the app.
 *
 * string - Any of the above as a string in JSON format.
 */
export type AppPostableMessage =
  | RequestMessage
  | ResponseMessage
  | ErrorMessage;

/**
 * Platform-specific delegate for posting a JSON string to the app.
 *
 * @param {string} message - The JSON string to post to the app.
 */
export type OnPostMessage = (message: string) => void;

/** Posts messages to the app from the add-on. */
export default class AppEndpoint {
  private onPostMessage?: OnPostMessage;

  /**
   * JSON message strings from the add-on that could not be posted to the app
   * because onPostMessage was null. Once onPostMessage is not null, these
   * will be passed to onPostMessage and cleared.
   */
  private messageStringsPendingOnPostMessageSet: Array<string>;

  /**
   * Gets the platform-specific delegate used by postMessage to post an add-on
   * JSON message string to the app.
   */
  public getOnPostMessage() {
    return this.onPostMessage;
  }

  /**
   * Sets the platform-specific delegate used by postMessage to post an add-on
   * JSON message string to the app. While onPostMessage is null, posted
   * messages will be queued. Once onPostMessage is not null, the queued
   * messages will be passed to onPostMessage automatically.
   */
  public setOnPostMessage(onPostMessage?: OnPostMessage) {
    if (onPostMessage === undefined || onPostMessage === null) {
      this.onPostMessage = onPostMessage;
      return;
    } else if (onPostMessage instanceof Function) {
      this.onPostMessage = onPostMessage;
    } else {
      throw new TypeError(
        `Unexpected onPostMessage type[${typeof onPostMessage}]`
      );
    }
    while (this.messageStringsPendingOnPostMessageSet.length > 0) {
      this.onPostMessage(this.messageStringsPendingOnPostMessageSet.shift());
    }
  }

  /**
   * Promises to the add-on from the app resulting from an add-on JSON message
   * string being passed to onPostMessage. The promises will be resolved or
   * rejected by a postMessage to the add-on from the app.
   */
  private pendingAppPromiseWithId: Map<string, PendingPromise>;

  /**
   * @param {Map<string, PendingPromise>} pendingAppPromiseWithId -
   * Promises to the add-on from the app resulting from an add-on JSON message
   * string being passed to onPostMessage. The promises will be resolved or
   * rejected by a postMessage to the add-on from the app.
   * @param {OnPostMessage} onPostMessage - Platform-specific delegate for
   * posting an add-on JSON message string to the app.
   * @param {Array<string>} messageStringsPendingOnPostMessageSet - Add-on JSON
   * message strings to pass to onPostMessage once it's not null.
   */
  constructor(
    pendingAppPromiseWithId: Map<string, PendingPromise>,
    onPostMessage?: OnPostMessage,
    messageStringsPendingOnPostMessageSet?: Array<string>
  ) {
    this.pendingAppPromiseWithId = pendingAppPromiseWithId;
    this.messageStringsPendingOnPostMessageSet =
      messageStringsPendingOnPostMessageSet ?? [];

    this.setOnPostMessage(onPostMessage);
  }

  /**
   * Posts a message to the app from the add-on.
   *
   * @param message {AppPostableMessage}
   *
   * @returns {Promise<unknown>} - Promise resolved with a ResponseMessage or
   * rejected with a RequestError from the app.
   */
  postMessage(message: AppPostableMessage): Promise<unknown | Posted> {
    return new Promise((onResolve, onReject) => {
      if (isResponseMessage(message) || isErrorMessage(message)) {
        this.postOrQueueMessageString(JSON.stringify(message));
        onResolve(posted);
      }

      if (isRequestMessage(message)) {
        if (
          !(
            isAppAction(message.actionName) ||
            isInternalAppAction(message.actionName)
          )
        ) {
          onReject(
            new RequestError(
              ErrorCode.BAD_REQUEST,
              `Invalid action name: ${message.actionName}`
            )
          );
          return;
        }

        if (message.uuid === undefined) {
          onReject(
            new RequestError(ErrorCode.BAD_REQUEST, `Unexpected undefined uuid`)
          );
          return;
        }

        // While collision is highly unlikely, it could happen. If it does happen,
        // we will generate a new ID until there is no collision.
        while (this.pendingAppPromiseWithId.has(message.uuid)) {
          // eslint-disable-next-line no-param-reassign
          message.uuid = uuid();
        }

        this.pendingAppPromiseWithId.set(message.uuid, {
          onResolve,
          onReject,
        });

        this.postOrQueueMessageString(JSON.stringify(message));
      } else {
        onReject(
          new RequestError(
            ErrorCode.BAD_REQUEST,
            `Unexpected message[${JSON.stringify(message)}]`
          )
        );
      }
    });
  }

  private postOrQueueMessageString(messageString: string) {
    if (this.onPostMessage) {
      this.onPostMessage(messageString);
    } else {
      this.messageStringsPendingOnPostMessageSet.push(messageString);
    }
  }
}
